import { useEffect, useState, useCallback } from "react";
import getPbjs from "../prebid/prebidSetup";
import gvljson from "./vendorlist.json"
import Logo from '../../assets/logo-jpg.webp';

// 1) Import iabtcf library
import { TCModel, GVL, TCString } from "@iabtechlabtcf/core";

// ---------------------------------------------------------
// Helper function to build a TCF string dynamically
// ---------------------------------------------------------

async function generateTCFString({ 
  purposeConsents = {}, 
  legitimateInterestConsents = {}, 
  vendorConsents = {}, 
  vendorLegitimateInterests = {}, 
  specialFeatureConsents = {}, 
  featureConsents = {} 
} = {}) {
  try {
    // Ensure correct GVL base URL
    GVL.baseUrl = 'https://www.trustnodes.com/';

    let gvl;
    try {
      //console.log("[DEBUG] Checking gvljson before creating GVL:", JSON.stringify(gvljson, null, 2));
      gvl = new GVL(gvljson);
      //console.log("[DEBUG] GVL instance created:", gvl);
    } catch (gvlInitError) {
      console.error("[ERROR] Failed to initialize GVL:", gvlInitError);
      return;
    }

    if (!gvl.vendors || Object.keys(gvl.vendors).length === 0) {
      console.error("GVL vendor list is missing or empty.");
      return;
    }

    const tcModel = new TCModel(gvl);

    tcModel.cmpId = 2;
    tcModel.cmpVersion = 1;
    tcModel.consentScreen = 1;
    tcModel.consentLanguage = "en"; 
    tcModel.policyVersion = 2;
    tcModel.isServiceSpecific = true;
    tcModel.publisherCountryCode = "US";

    // Set Purpose Legitimate Interests (Fixing Missing Entries)
    if (tcModel.purposeLegitimateInterests) {
      Object.keys(legitimateInterestConsents).forEach(purposeId => {
        tcModel.purposeLegitimateInterests.set(parseInt(purposeId), Boolean(legitimateInterestConsents[purposeId]));
      });
    } else {
      console.warn("[WARNING] tcModel.purposeLegitimateInterests is undefined.");
    }

    // Set Purpose Consents
    if (tcModel.purposeConsents) {
      Object.keys(purposeConsents).forEach(purposeId => {
        tcModel.purposeConsents.set(parseInt(purposeId), Boolean(purposeConsents[purposeId]));
      });
    }

    // Set Vendor Consents
    if (tcModel.vendorConsents) {
      Object.keys(vendorConsents).forEach(vendorId => {
        tcModel.vendorConsents.set(parseInt(vendorId), Boolean(vendorConsents[vendorId]));
      });
    }

    // Set Vendor Legitimate Interests
    if (tcModel.vendorLegitimateInterests) {
      Object.keys(vendorLegitimateInterests).forEach(vendorId => {
        tcModel.vendorLegitimateInterests.set(parseInt(vendorId), Boolean(vendorLegitimateInterests[vendorId]));
      });
    }

    //Set Special Feature Opt-Ins
    if (tcModel.specialFeatureOptIns) {
      Object.keys(specialFeatureConsents).forEach(featureId => {
        tcModel.specialFeatureOptIns.set(parseInt(featureId), Boolean(specialFeatureConsents[featureId]));
      });
    }

    //Set Features (General Features such as fraud prevention)
    if (tcModel.features) {
      Object.keys(featureConsents).forEach(featureId => {
        tcModel.features.set(parseInt(featureId), Boolean(featureConsents[featureId]));
      });
    }

    let encodedString;
    try {
      encodedString = TCString.encode(tcModel);

      // **Fix Encoding Issues**: Ensure Base64URL formatting
      encodedString = encodedString
        .replace(/\+/g, '-')  // Replace '+' with '-'
        .replace(/\//g, '_')  // Replace '/' with '_'
        .replace(/=+$/, '');  // Remove padding '='

    } catch (encodingError) {
      console.error("[ERROR] Failed to encode TCModel:", encodingError);
      return;
    }

    //console.log("[CMP] Generated TCF String:", encodedString);
    return encodedString;
  } catch (error) {
    console.error("[ERROR] generateTCFString failed:", error);
  }
}


const ConsentManager = () => {
  const [showCustomOptions, setShowCustomOptions] = useState(false);
  const [isShowBanner, setShowBanner] = useState(true); 

  // A simple local representation of user consent.
  const [consent, setConsent] = useState({
    gdprApplies: 1, 
    tcString: "", 
    purpose: {
      consents: {}, 
      legitimateInterests: {}, 
    },
    specialFeatureOptIns: {}, 
    features: {}, 
    vendor: {
      consents: {}, 
      legitimateInterests: {}, 
    },
  });


  // ---------------------------------------------------------
  // 3) Save user consent from your banner or preferences
  // ---------------------------------------------------------

const saveConsent = async (newConsent) => {
  let tcfString = "";

  try {
    tcfString = await generateTCFString({
      purposeConsents: newConsent.purpose.consents,
      legitimateInterestConsents: newConsent.purpose.legitimateInterests,
      vendorConsents: newConsent.vendor.consents,
      vendorLegitimateInterests: newConsent.vendor.legitimateInterests,
      specialFeatureConsents: newConsent.specialFeatureOptIns, // Added Special Features
      featureConsents: newConsent.features, // Added Features
    });
  } catch (error) {
    console.error("[CMP] Error generating TCF string, using fallback.");
    tcfString = "000000"; // Fallback TCF string
  }

  const updatedConsent = {
    gdprApplies: 1,
    tcString: tcfString || "",
    purpose: {
      consents: newConsent.purpose.consents || {},
      legitimateInterests: newConsent.purpose.legitimateInterests || {},
    },
    specialFeatureOptIns: newConsent.specialFeatureOptIns || {}, // Ensuring Special Features are included
    features: newConsent.features || {}, // Ensuring Features are included
    vendor: {
      consents: newConsent.vendor.consents || {},
      legitimateInterests: newConsent.vendor.legitimateInterests || {},
    },
  };

  // Update State
  setConsent(updatedConsent);

  // Update CMP via `__tcfapi`
  window.__tcfapi("setConsent", 2, () => {}, updatedConsent);

  setShowBanner(false);
};

  useEffect(() => {
    document.body.style.overflow = isShowBanner ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isShowBanner]);

  // ---------------------------------------------------------
  // 4) Render a simple consent banner
  // ---------------------------------------------------------

  if (!isShowBanner) {
    return null; 
  }
 
  return (
    
      <div className="fixed  inset-0 bg-black bg-opacity-50 z-40">
<div className="fixed bottom-0  border-t border-gray-500 left-1/2 transform -translate-x-1/2 w-full max-w-none bg-white border border-gray-300 text-gray-900 p-6 shadow-lg z-50">

     
      <ConsentButtons saveConsent={saveConsent} consent={consent} isShowBanner={isShowBanner} showCustomOptions={showCustomOptions} setShowCustomOptions={setShowCustomOptions} />

      <CustomizePreferences
        showCustomOptions={showCustomOptions}
        setShowCustomOptions={setShowCustomOptions}
        consent={consent}
        setConsent={setConsent}
        saveConsent={saveConsent}
      />
    </div>
    </div>

  );

};

// ---------------------------------------------------------
// 5) Sub-components for Banner UI
// ---------------------------------------------------------
const ConsentMessage = () => (
  <div className="mb-4">
  {/* Cookies Notice */}
  <p className="mt-2 leading-relaxed text-[15px] flex items-start" >
 

    We and our partners use cookies and similar technologies to enhance your browsing experience, analyze site traffic, personalize content, and serve tailored advertisements.
  </p>

  {/* Privacy Policy Link */}
  <p className="mt-2 leading-relaxed text-[15px]">
    Your consent allows us to process data such as browsing behavior and unique identifiers to improve our services. To learn more about these methods, you can view our
    <a href="/privacy-policy" className="text-blue-600 hover:underline mx-1">
      Privacy Policy
    </a>.
  </p>

  {/* GDPR Compliance */}
  <p className="mt-2 leading-relaxed text-[15px]">
     We comply with GDPR and other privacy regulations to ensure your data remains secure. You can manage your preferences at any time to customize your privacy settings.
  </p>
</div>


);


const ConsentButtons = ({ saveConsent, consent, showCustomOptions, setShowCustomOptions, isShowBanner }) => {
  if (!isShowBanner) return null; // Exit early if banner should not be shown


  const handleAcceptAll = async () => {
    
    // Define all required purpose consents, ensuring full coverage
    const allPurposeConsents = {
      1: true,  // Store and/or access information on a device
      2: true,  // Use limited data to select advertising
      3: true,  // Create profiles for personalised advertising
      4: true,  // Use profiles to select personalised advertising
      5: true,  // Create profiles to personalise content
      6: true,  // Use profiles to select personalised content
      7: true,  // Measure advertising performance
      8: true,  // Measure content performance
      9: true,  // Understand audiences through statistics or data
      10: true, // Develop and improve services
      11: true  // Use limited data to select content
  };

  // Special Features (if SSPs require fingerprinting or precise location)
  const allSpecialFeaturesConsents = {
      1: true, // Use precise geolocation data
      2: true  // Actively scan device characteristics for identification (fingerprinting)
  };

  // Features (SmartyAds requires Fraud Prevention)
  const allFeaturesConsents = {
      3: true //Ensuring security, preventing fraud, and debugging (Required by SmartyAds)
  };

  // Legitimate Interest Purposes (SSPs often justify data use under "legitimate interest")
  const allLegitimateInterests = {
    7: true,  // Measure advertising performance (If vendors justify it)
    8: true,  // Measure content performance (If vendors justify it)
    9: true,  // Understand audiences through statistics or data
    10: true  // Develop and improve services
  };


  // Vendor consents (Example: SmartyAds, Google AdX, PubMatic, OpenX, Xandr)
  const allVendorConsents = {
      534: true,   // SmartyAds example
  };

  // Save consent with additional special features and legitimate interest
  saveConsent({
    gdprApplies: 1,
    tcString: "", // Will be dynamically generated
    purpose: {
      consents: allPurposeConsents,
      legitimateInterests: allLegitimateInterests,
    },
    specialFeatureOptIns: allSpecialFeaturesConsents, // Ensure Special Features are included
    features: allFeaturesConsents, // Ensure Features are included
    vendor: {
      consents: allVendorConsents,
      legitimateInterests: allLegitimateInterests,
    },

  });
  };

return (
  showCustomOptions ? null : (
<div className="flex flex-col md:flex-row items-center md:items-start gap-6 mt-6 w-full">
  
  {/* Consent Message (Left on Desktop, Full-Width on Mobile) */}
  <div className="flex-1">
  <h2 className="text-1xl font-semibold leading-relaxed text-gray-900 tracking-tight flex items-start"> 

  <svg 
  xmlns="http://www.w3.org/2000/svg" 
  className="h-5 w-4 text-[#3D7D63] flex-shrink-0 mr-3 mt-[2px]"
  viewBox="0 0 32 32" 
  fill="none"
  stroke="currentColor"
  strokeWidth="2"
  strokeLinecap="round"
  strokeLinejoin="round"
>
  <rect x="6" y="6" width="20" height="24" rx="3" fill="currentColor"/>  
  <line x1="10" y1="12" x2="22" y2="12" stroke="white" strokeWidth="2"/>  
  <line x1="10" y1="18" x2="22" y2="18" stroke="white" strokeWidth="2"/>  
  <circle cx="16" cy="24" r="3" fill="white"/>  
  <line x1="14.5" y1="24" x2="16" y2="25.5" stroke="currentColor" strokeWidth="2"/>  
  <line x1="16" y1="25.5" x2="18" y2="22.5" stroke="currentColor" strokeWidth="2"/>  
</svg>

Manage Privacy Preferences</h2>
    <ConsentMessage />
  </div>

  {/* Buttons (Right on Desktop, Stacked) */}
  <div className="w-full md:w-auto mt-10 flex flex-col items-center gap-4 md:items-start">
    <button       
      className="mb-2 w-full px-6 py-1 bg-[#2F8F72] hover:bg-[#5c9889] text-white font-medium text-lg shadow-md transition-all"
      onClick={handleAcceptAll}
    >
      Accept All
    </button>

    <button
      className="w-full mt-4 px-6 py-1 bg-white border border-gray-300 hover:bg-gray-200 text-black font-medium text-lg transition-all"
      onClick={() => setShowCustomOptions(true)}
    >
      Customize Preferences
    </button>
  </div>

</div>


  )
);

};

const CustomizePreferences = ({
  showCustomOptions,
  setShowCustomOptions,
  consent,
  setConsent,
  saveConsent,
}) => {

const handleLegitimateInterestChange = (purposeId, isChecked) => {
  setConsent((prevConsent) => ({
    ...prevConsent,
    purpose: {
      ...prevConsent.purpose,
      legitimateInterests: {
        ...prevConsent.purpose?.legitimateInterests,
        [purposeId]: isChecked, // Allows users to opt out of LI
      },
    },
  }));
};



  const handleConsentChange = (purposeId, checked) => {
    setConsent((prevConsent) => ({
      ...prevConsent,
      purpose: {
        ...prevConsent.purpose,
        consents: {
          ...prevConsent.purpose?.consents,
          [purposeId]: checked,
        },
      },
    }));
  };

  //Handle "Reject All" (Only keep essential purposes)
  const handleRejectAll = () => {
    console.log("[CMP] User rejected all non-essential purposes. Keeping only security and fraud prevention.");

    const rejectedConsent = {
      gdprApplies: 1,
      tcString: "", // Will be dynamically generated
      purpose: {
        consents: {
          1: true, // Essential: Store and/or access information on a device (Required)
        },
        legitimateInterests: {
          1: true // Essential: Storage & Access via Legitimate Interest
        },
      },
      specialFeatureOptIns: {
        1: true, // Essential: Geolocation (Fraud Prevention)
        2: true  // Essential: Fingerprinting (Bot Detection, Payment Fraud Prevention)
      },
      features: {
        3: true // Essential: Security & Fraud Prevention (SmartyAds, Google, OpenX)
      },
      vendor: {
        consents: {}, // No advertising vendors allowed
        legitimateInterests: {}, // No legitimate interest for ads
      },
    };

    saveConsent(rejectedConsent);
    console.log("[CMP] Rejected all non-essential purposes. Only security & fraud prevention enabled.");
  };


  return (
    <>
 

      {showCustomOptions && (
<div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center z-50">
  <div className="w-full max-w-none md:max-w-lg bg-white shadow-lg border border-gray-200 
      max-h-[80vh] flex flex-col md:top-1/2 md:-translate-y-1/2 overflow-y-auto md:mb-20">


    
    {/*Fixed Header with Logo */}
  <div className="sticky top-0 bg-white z-10 border-b py-2 flex flex-col items-start">
    <img src={Logo} alt="Logo" className="h-10 mb-2" /> {/* Replace with your logo */}
  </div>


    {/*Scrollable Content (Expands to Push Buttons to Bottom) */}
    <div className="overflow-y-auto flex-1 px-6 py-4 max-h-[60vh]">
    
      <div className="mb-6 border-b pb-4">

        <h2 className="text-2xl font-semibold text-gray-900">Manage Privacy Preferences</h2>

          <p className=" text-sm leading-relaxed mt-2 ">
            Your privacy is important to us. We use cookies and similar technologies to improve your experience, 
            analyze site traffic, and personalize content. Below, you can review and customize how your data is used.
          </p>
        </div>

       {/* Essential Services */}
      <div className="mb-6 border-b pb-4">
        <h3 className="text-lg font-semibold text-gray-800">Essential Services (Required)</h3>
        <p className="text-sm mb-3 leading-relaxed">
          These services ensure secure transactions, fraud prevention, and login management. This data is not used for advertising.
        </p>
        <div className="space-y-3">
          <label className="flex items-center justify-between text-sm">
            <span>Payments & Transactions (Stripe, Coinbase)</span>
            <input type="checkbox" checked disabled className="h-5 w-5 text-gray-400 cursor-not-allowed" />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Login & Subscription Management</span>
            <input type="checkbox" checked disabled className="h-5 w-5 text-gray-400 cursor-not-allowed" />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Security & Fraud Prevention (Required)</span>
            <input type="checkbox" checked disabled className="h-5 w-5 text-gray-400 cursor-not-allowed" />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Geolocation for Fraud Detection (Required)</span>
            <input type="checkbox" checked disabled className="h-5 w-5 text-gray-400 cursor-not-allowed" />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Device Identification for Security (Required)</span>
            <input type="checkbox" checked disabled className="h-5 w-5 text-gray-400 cursor-not-allowed" />
          </label>
        </div>
      </div>

      {/* Legitimate Interest Opt-Out */}
      <div className="mb-6 border-b pb-4">
        <h3 className="text-lg font-semibold text-gray-800">Legitimate Interest Processing</h3>
        <p className="text-sm mb-3 leading-relaxed">
          Some data is processed under legitimate interest for service improvements, analytics, and non-intrusive advertising.
          You can opt out of these processing activities below.
        </p>
        <div className="space-y-3">
          <label className="flex items-center justify-between text-sm">
            <span>Measure Advertising Performance</span>
            <input
              type="checkbox"
              checked={consent.purpose.legitimateInterests?.[7] ?? true}
              onChange={(e) => handleLegitimateInterestChange(7, e.target.checked)}
              className="h-5 w-5 text-blue-500"
            />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Measure Content Performance</span>
            <input
              type="checkbox"
              checked={consent.purpose.legitimateInterests?.[8] ?? true}
              onChange={(e) => handleLegitimateInterestChange(8, e.target.checked)}
              className="h-5 w-5 text-blue-500"
            />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Understand Audiences Through Statistics</span>
            <input
              type="checkbox"
              checked={consent.purpose.legitimateInterests?.[9] ?? true}
              onChange={(e) => handleLegitimateInterestChange(9, e.target.checked)}
              className="h-5 w-5 text-blue-500"
            />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Develop and Improve Services</span>
            <input
              type="checkbox"
              checked={consent.purpose.legitimateInterests?.[10] ?? true}
              onChange={(e) => handleLegitimateInterestChange(10, e.target.checked)}
              className="h-5 w-5 text-blue-500"
            />
          </label>
        </div>
      </div>


      {/* Optionals */}
      <div className="mb-6 border-b pb-4">
        <h3 className="text-lg font-semibold text-gray-900">Advertising & Personalization</h3>
        <p className="text-sm mb-3 leading-relaxed">
          Advertising supports our platform. You can choose between basic ads or personalized ads tailored to your interests.
        </p>
        <div className="space-y-3">
          <label className="flex items-center justify-between text-sm">
            <span>Use Limited Data to Select Advertising</span>
            <input
              type="checkbox"
              checked={consent.purpose.consents?.[2] || false}
              onChange={(e) => handleConsentChange(2, e.target.checked)}
              className="h-5 w-5 text-yellow-500"
            />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Create Profiles for Personalized Advertising</span>
            <input
              type="checkbox"
              checked={consent.purpose.consents?.[3] || false}
              onChange={(e) => handleConsentChange(3, e.target.checked)}
              className="h-5 w-5 text-yellow-500"
            />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Use Profiles to Select Personalized Advertising</span>
            <input
              type="checkbox"
              checked={consent.purpose.consents?.[4] || false}
              onChange={(e) => handleConsentChange(4, e.target.checked)}
              className="h-5 w-5 text-yellow-500"
            />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Create Profiles to Personalize Content</span>
            <input
              type="checkbox"
              checked={consent.purpose.consents?.[5] || false}
              onChange={(e) => handleConsentChange(5, e.target.checked)}
              className="h-5 w-5 text-yellow-500"
            />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Use Profiles to Select Personalized Content</span>
            <input
              type="checkbox"
              checked={consent.purpose.consents?.[6] || false}
              onChange={(e) => handleConsentChange(6, e.target.checked)}
              className="h-5 w-5 text-yellow-500"
            />
          </label>
        </div>
      </div>
      <div className="mb-6 border-b pb-4">
        <h3 className="text-lg font-semibold text-gray-800">Analytics & Performance</h3>
        <p className="text-sm mb-3 leading-relaxed">
          Enabling analytics helps us improve site performance and user experience.
        </p>
        <div className="space-y-3">
          <label className="flex items-center justify-between text-sm">
            <span>Measure Advertising Performance</span>
            <input
              type="checkbox"
              checked={consent.purpose.consents?.[7] || false}
              onChange={(e) => handleConsentChange(7, e.target.checked)}
              className="h-5 w-5 text-blue-500"
            />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Measure Content Performance</span>
            <input
              type="checkbox"
              checked={consent.purpose.consents?.[8] || false}
              onChange={(e) => handleConsentChange(8, e.target.checked)}
              className="h-5 w-5 text-blue-500"
            />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Understand Audiences Through Data</span>
            <input
              type="checkbox"
              checked={consent.purpose.consents?.[9] || false}
              onChange={(e) => handleConsentChange(9, e.target.checked)}
              className="h-5 w-5 text-blue-500"
            />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Develop & Improve Services</span>
            <input
              type="checkbox"
              checked={consent.purpose.consents?.[10] || false}
              onChange={(e) => handleConsentChange(10, e.target.checked)}
              className="h-5 w-5 text-blue-500"
            />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Use Limited Data to Select Content</span>
            <input
              type="checkbox"
              checked={consent.purpose.consents?.[11] || false}
              onChange={(e) => handleConsentChange(11, e.target.checked)}
              className="h-5 w-5 text-blue-500"
            />
          </label>
        </div>
      </div>

    </div> {/* End Scrollable Content */}

    {/*Fixed Bottom Buttons */}
    <div className="bg-white border-t p-4">
      <div className="flex gap-4 w-full">
        <button
          className="w-1/2 py-2 bg-white border border-gray-300 hover:bg-gray-200 text-black font-medium text-lg transition-all"
          onClick={handleRejectAll}
        >
          Reject All
        </button>
        <button
          className="w-1/2 py-2 bg-[#2F8F72] hover:bg-[#5c9889] text-white font-medium text-lg shadow-md transition-all"
          onClick={() => saveConsent(consent)}
        >
          Save Preferences
        </button>
      </div>
      {/* Close Button */}
      <button
        className="mt-4 block text-center w-full text-gray-500 text-sm hover:underline"
        onClick={() => setShowCustomOptions(false)}
      >
        Back to Preferences
      </button>
    </div>

  </div>
</div>




)}

    </>
  );
};



export default ConsentManager;
